import { chapterNumber } from "../lib/timeline/book/ChapterId";
import type { PlaybackBounds } from "../lib/timeline/playback";
import { mergeCharacters, mergePlaces } from "./join-chapter-cast";
import type { RailLayout } from "./rail-branches";
import type { CompiledChapter, TimelineEvent } from "./schema/compiled";

export interface TimelineSpan {
  id: string;
  label: string;
  startMs: number;
  endMs: number;
}

export const WHOLE_BOOK_SPAN_ID = "book";

/** Play inside the span. The end is where the next chapter starts. */
export function spanPlaybackBounds(span: TimelineSpan): PlaybackBounds {
  return {
    startMs: span.startMs,
    endMs: Math.max(span.startMs, span.endMs - 1),
  };
}

export function chapterTimelineSpan(chapter: CompiledChapter): TimelineSpan {
  const last = chapter.eventsByJourney.book.at(-1);
  const endMs = last === undefined ? 0 : last.playbackMs + last.durationMs;
  const number = chapterNumber(chapter.chapterId) ?? 1;
  return {
    id: chapter.chapterId,
    label: `Chapter ${number}`,
    startMs: 0,
    endMs,
  };
}

export function joinCompiledChapters(chapters: readonly CompiledChapter[]): {
  chapter: CompiledChapter;
  spans: TimelineSpan[];
} {
  if (chapters.length === 0) {
    throw new Error("No chapters to join.");
  }
  const ordered = [...chapters].sort(byChapter);
  let offset = 0;
  const shifted = ordered.map((chapter) => {
    const next = shiftChapter(chapter, offset);
    offset += chapterDurationMs(chapter);
    return next;
  });
  return {
    chapter: mergeShifted(ordered, shifted),
    spans: spansFor(ordered, offset),
  };
}

function byChapter(a: CompiledChapter, b: CompiledChapter): number {
  return (chapterNumber(a.chapterId) ?? 0) - (chapterNumber(b.chapterId) ?? 0);
}

function chapterDurationMs(chapter: CompiledChapter): number {
  const last = chapter.eventsByJourney.book.at(-1);
  if (last === undefined) {
    return 0;
  }
  return last.playbackMs + last.durationMs;
}

function shiftChapter(chapter: CompiledChapter, offsetMs: number): CompiledChapter {
  return {
    ...chapter,
    eventsByJourney: {
      book: shiftEvents(chapter.eventsByJourney.book, offsetMs),
      universe: shiftEvents(chapter.eventsByJourney.universe, offsetMs),
    },
    railsByJourney: {
      book: shiftRail(chapter.railsByJourney.book, offsetMs, chapter.chapterId),
      universe: shiftRail(
        chapter.railsByJourney.universe,
        offsetMs,
        chapter.chapterId,
      ),
    },
  };
}

function shiftEvents(events: TimelineEvent[], offsetMs: number): TimelineEvent[] {
  if (offsetMs === 0) {
    return [...events];
  }
  return events.map((event) => ({
    ...event,
    playbackMs: event.playbackMs + offsetMs,
  }));
}

function shiftRail(rail: RailLayout, offsetMs: number, chapterId: string): RailLayout {
  return {
    branches: rail.branches.map((branch) => ({
      ...branch,
      id: `${chapterId}-${branch.id}`,
      startMs: branch.startMs + offsetMs,
      endMs: branch.endMs + offsetMs,
    })),
    stays: rail.stays.map((stay) => ({
      ...stay,
      startMs: stay.startMs + offsetMs,
      endMs: stay.endMs + offsetMs,
    })),
  };
}

function spansFor(chapters: readonly CompiledChapter[], endMs: number): TimelineSpan[] {
  let cursor = 0;
  const parts = chapters.map((chapter, index) => {
    const startMs = cursor;
    cursor += chapterDurationMs(chapter);
    const number = chapterNumber(chapter.chapterId) ?? index + 1;
    return {
      id: chapter.chapterId,
      label: `Chapter ${number}`,
      startMs,
      endMs: cursor,
    };
  });
  return [
    { id: WHOLE_BOOK_SPAN_ID, label: "Whole book", startMs: 0, endMs },
    ...parts,
  ];
}

function mergeShifted(
  originals: readonly CompiledChapter[],
  shifted: readonly CompiledChapter[],
): CompiledChapter {
  const first = shifted[0];
  return {
    bookId: first.bookId,
    chapterId: WHOLE_BOOK_SPAN_ID,
    title: first.title,
    chapterTitles: originals.map((chapter) => ({
      chapterId: chapter.chapterId,
      title: chapter.title,
    })),
    characters: mergeCharacters(originals),
    places: mergePlaces(originals),
    scenes: shifted.flatMap((chapter) => chapter.scenes),
    eventsByJourney: {
      book: shifted.flatMap((chapter) => chapter.eventsByJourney.book),
      universe: shifted.flatMap((chapter) => chapter.eventsByJourney.universe),
    },
    railsByJourney: {
      book: concatRails(shifted.map((chapter) => chapter.railsByJourney.book)),
      universe: concatRails(shifted.map((chapter) => chapter.railsByJourney.universe)),
    },
  };
}

function concatRails(rails: RailLayout[]): RailLayout {
  return {
    branches: rails.flatMap((rail) => rail.branches),
    stays: rails.flatMap((rail) => rail.stays),
  };
}
